import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { isActive as isRouteActive, IsActiveMatchOptions, NavigationEnd, Router } from '@angular/router';
import { ServicemeshNavigationService } from '@helpers/components/navigation/navigation.service';
import { ServicemeshNavigationItem } from '@helpers/components/navigation/navigation.types';
import { ServicemeshVerticalNavigationBasicItemComponent } from '@helpers/components/navigation/vertical/components/basic/basic.component';
import { ServicemeshVerticalNavigationCollapsableItemComponent } from '@helpers/components/navigation/vertical/components/collapsable/collapsable.component';
import { ServicemeshVerticalNavigationDividerItemComponent } from '@helpers/components/navigation/vertical/components/divider/divider.component';
import { ServicemeshVerticalNavigationGroupItemComponent } from '@helpers/components/navigation/vertical/components/group/group.component';
import { ServicemeshVerticalNavigationSpacerItemComponent } from '@helpers/components/navigation/vertical/components/spacer/spacer.component';
import { ServicemeshVerticalNavigationComponent } from '@helpers/components/navigation/vertical/vertical.component';
import { filter, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'servicemesh-vertical-navigation-aside-item',
  templateUrl: './aside.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    MatTooltipModule,
    MatIconModule,
    ServicemeshVerticalNavigationBasicItemComponent,
    ServicemeshVerticalNavigationCollapsableItemComponent,
    ServicemeshVerticalNavigationDividerItemComponent,
    ServicemeshVerticalNavigationGroupItemComponent,
    ServicemeshVerticalNavigationSpacerItemComponent,
  ],
})
export class ServicemeshVerticalNavigationAsideItemComponent
  implements OnChanges, OnInit, OnDestroy
{
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _router = inject(Router);
  private readonly _servicemeshNavigationService = inject(ServicemeshNavigationService);

  /* eslint-disable @typescript-eslint/naming-convention */
  static ngAcceptInputType_autoCollapse: BooleanInput; // NOSONAR
  static ngAcceptInputType_skipChildren: BooleanInput; // NOSONAR
  /* eslint-enable @typescript-eslint/naming-convention */

  @Input() activeItemId!: string;
  @Input() autoCollapse!: boolean;
  @Input() item!: ServicemeshNavigationItem;
  @Input() name!: string;
  @Input() skipChildren!: boolean;

  active: boolean = false;
  private _servicemeshVerticalNavigationComponent!: ServicemeshVerticalNavigationComponent;
  private readonly _unsubscribeAll: Subject<unknown> = new Subject<unknown>();

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On changes
   *
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    // Active item id
    if ('activeItemId' in changes) {
      // Mark if active
      this._markIfActive(this._router.url);
    }
  }

  /**
   * On init
   */
  ngOnInit(): void {
    // Mark if active
    this._markIfActive(this._router.url);

    // Attach a listener to the NavigationEnd event
    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe((event: NavigationEnd) => {
        // Mark if active
        this._markIfActive(event.urlAfterRedirects);
      });

    // Get the parent navigation component
    this._servicemeshVerticalNavigationComponent = this._servicemeshNavigationService.getComponent(
      this.name
    );

    // Subscribe to onRefreshed on the navigation component
    this._servicemeshVerticalNavigationComponent.onRefreshed
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        // Mark for check
        this._changeDetectorRef.markForCheck();
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: ServicemeshNavigationItem): string | number {
    return item.id || index;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Build match options for Router.isActive based on exactMatch flag
   *
   * @param exactMatch
   * @private
   */
  private _buildMatchOptions(exactMatch = false): IsActiveMatchOptions {
    const matchOptions: IsActiveMatchOptions = {
      paths: 'subset',
      queryParams: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
    };
    if (exactMatch) {
      matchOptions.paths = 'exact';
      matchOptions.queryParams = 'exact';
    }
    return matchOptions;
  }

  /**
   * Check if the given item has the given url
   * in one of its children
   *
   * @param item
   * @param currentUrl
   * @private
   */
  private _hasActiveChild(item: ServicemeshNavigationItem, currentUrl: string): boolean {
    const children = item.children;

    if (!children) {
      return false;
    }

    for (const child of children) {
      if (child.children) {
        if (this._hasActiveChild(child, currentUrl)) {
          return true;
        }
      }

      // Skip items other than 'basic'
      if (child.type !== 'basic') {
        continue;
      }

      // Check if the child has a link and is active
      if (
        child.link &&
        isRouteActive(child.link, this._router, this._buildMatchOptions(child.exactMatch))()
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Decide and mark if the item is active
   *
   * @private
   */
  private _markIfActive(currentUrl: string): void {
    // Check if the activeItemId is equals to this item id
    this.active = this.activeItemId === this.item.id;

    // If the aside has a children that is active,
    // always mark it as active
    if (this._hasActiveChild(this.item, currentUrl)) {
      this.active = true;
    }

    // Mark for check
    this._changeDetectorRef.markForCheck();
  }
}
